import { Button, Card, Checkbox, DatePicker, Descriptions, Divider, Form, Input, Modal, Select, TableProps, Tooltip } from 'antd'
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { useLoading } from '../../context/LoadingContext'
import { toastError, toastSuccess } from '../../utils/toast'
import { CheckOutlined } from '@ant-design/icons'
import { apiAddInvoice, apiAddInvoiceAdapterByInvoiceId, apiAddTransactionConnection, apiGetListCompanies, apiGetListInvoices, apiGetListInvoiceTemplateItems, apiGetListInvoiceTemplates, apiGetListPackagesConnection, apiGetPrintInvoiceAdapter } from '../../api'
import defaultParams, { TRANSACTION_CONNECTION_TYPE_CONNECTION, TRANSACTION_CONNECTION_TYPE_EXTENSION } from '../../assets/constants/default_params'
import { ICompany } from '../../models/company.modal'
import { filterOptionByLabel } from '../../utils/filter-utils'
import CustomTable from '../../components/table'
import { IPartnerPackage } from '../../models/package.modal'
import { authSelector } from '../../redux/slices/authSlice'
import { useSelector } from 'react-redux'
import { saveAs } from 'file-saver'
import { formatCurrency, parseSyntax, reNumberMonthDataPack } from '../../utils/string-utils'
import { renderType } from './method'
import { renderProductNature } from './order'
import { renderInvoiceStatus } from '../invoice/history'
import moment from 'moment'

export default function Connect() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [partnerPackageOptions, setPartnerPackageOptions] = useState<{ label: string, value: Number }[]>([])
    const [invoiceTemplates, setInvoiceTemplates] = useState<any>([])
    const [invoiceTemplateItems, setInvoiceTemplateItems] = useState<any>([])
    const [transactionConnection, setTransactionConnection] = useState<any>({
        transactions: [],
        total: 0,
    })
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [cpCode, setCpCode] = useState<string>('')
    const [filterPack, setFilterPack] = useState<string>('')
    const [isOpenSelectDate, setIsOpenSelectDate] = useState(false)
    const [modalInvoiceExport, setModalInvoiceExport] = useState<any>({ isOpen: false, data: null })
    const [invoiceExportValue, setInvoiceExportValue] = useState<any>({ customer_name: '', invoice_template_id: 0 })
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
    const [showModalConfirmExport, setShowModalConfirmExport] = useState<boolean>(false)
    const { setIsLoading } = useLoading()
    const [formConnect] = Form.useForm()
    const { account } = useSelector(authSelector)

    const transactionConnectionResponseColumns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'phoneAgency',
            title: 'SĐT báo KH',
            dataIndex: 'phoneAgency',
        },
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'phoneCustomer',
            title: 'SĐT Khách hàng',
            dataIndex: 'phoneCustomer',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'packType',
            title: 'Loại gói cước',
            dataIndex: 'packType',
            render: packType => renderType(packType),
        },
        {
            key: 'startDate',
            title: 'Ngày đấu nối',
            dataIndex: 'startDate',
        },
        {
            key: 'message',
            title: 'Kết quả',
            dataIndex: 'message',
        },
        {
            key: 'customer_name',
            title: 'Khách hàng',
            render: data => {
                return data.transaction
                    ? data.customerName
                        ? data.customerName
                        : data.invoice
                            ? data.invoice.customer_name
                            : null
                    : null
            },
        },
        {
            key: 'price',
            title: 'Giá',
            render: data => data.transaction ? formatCurrency(data.transaction.price) : null
        },
        {
            key: 'invoice',
            title: 'Trạng thái HĐ',
            render: data => {
                return data.invoice ? renderInvoiceStatus(data.invoice.status) : null
            },
        },
        (account?.canGetListInvoices) ? {
            key: 'update',
            title: 'Xuất hóa đơn',
            render: data => data.transaction ? (account?.canGetListInvoices && (
                <Tooltip title="Thông tin hoá đơn">
                    <Button
                        size='small'
                        type={data.invoice ? 'primary' : 'default'}
                        style={!data.invoice ? {} : data.invoice?.status === 'SUCCESS' ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } :
                            data.invoice?.status === 'CREATED' ? {} : { backgroundColor: '#f5222d', borderColor: '#f5222d' }}
                        onClick={() => setModalInvoiceExport({ isOpen: true, data })}
                        icon={<PrinterOutlined />}
                    />
                </Tooltip>
            )) : <></>,
        } : {},
    ]

    const invoiceTemplateItemsColumns: TableProps<any>['columns'] = [
        {
            key: 'product_line_number',
            title: 'STT',
            dataIndex: 'product_line_number',
        },
        {
            key: 'description',
            title: 'Tên hàng hoá',
            dataIndex: 'description',
        },
        {
            key: 'unit',
            title: 'Đơn vị',
            dataIndex: 'unit',
        },
        {
            key: 'quantity',
            title: 'Số lượng',
            dataIndex: 'quantity',
        },
        {
            key: 'unit_price',
            title: 'Đơn giá',
            dataIndex: 'unit_price',
            render: unit_price => formatCurrency(unit_price),
        },
        {
            key: 'product_nature',
            title: 'Loại sản phẩm',
            dataIndex: 'product_nature',
            render: product_nature => renderProductNature(product_nature),
        },
    ]

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data?.companies === null) {
                return
            }
            const options = resp.data.companies.map((item: ICompany) => ({
                label: item.name,
                value: item.code,
            }))
            setCompanyOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchPartnerPackages = useCallback(async () => {
        if (!cpCode) return
        try {
            const resp = await apiGetListPackagesConnection(cpCode)
            if (resp.data?.partner_packages === null) {
                return
            }
            const options = resp.data.partner_packages.map((item: IPartnerPackage) => {
                let label = item.name
                let value = `${item.code}::${item.type}::${item.price}`
                if (item.type === TRANSACTION_CONNECTION_TYPE_CONNECTION) {
                    label += '_Đấu mới'
                }
                if (item.type === TRANSACTION_CONNECTION_TYPE_EXTENSION) {
                    label += '_Gia hạn'
                }
                return { label, value }
            })
            setPartnerPackageOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [cpCode])

    const fetchInvoiceTemplates = useCallback(async () => {
        if (!account?.canGetListInvoiceTemplates) return
        try {
            const respInvoice = await apiGetListInvoiceTemplates()
            setInvoiceTemplates(respInvoice.data.templates)
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchInvoiceTemplateItems = useCallback(async () => {
        if (!account?.canGetListInvoiceTemplateItems) return
        try {
            const respInvoice = await apiGetListInvoiceTemplateItems()
            setInvoiceTemplateItems(respInvoice.data.items)
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
        fetchPartnerPackages()
        fetchInvoiceTemplates()
        fetchInvoiceTemplateItems()
    }, [fetchCompanies, fetchPartnerPackages, fetchInvoiceTemplates, fetchInvoiceTemplateItems])

    const fetchInvoices = useCallback(async (trans: any) => {
        if (!account?.canGetListInvoices) return
        const idTrans = [] as string[]
        trans.map((tran: any) => {
            const id = tran.transaction?.id
            if (id) {
                idTrans.push(`'${id}'`)
            }
            return tran
        })
        if (idTrans.length > 0) {
            const respInvoice = await apiGetListInvoices({
                page: 1,
                size: idTrans.length,
                condition: `tran_id IN (${idTrans.toString()})`
            })
            if (Number(respInvoice.data?.invoices?.length || 0) > 0) {
                const transactions = {
                    transactions: trans.map((tran: any) => {
                        const id = tran.transaction?.id
                        if (!id) return tran
                        const invoice = respInvoice.data?.invoices?.find((inv: any) => inv.tran_id === id)
                        return { ...tran, invoice }
                    }),
                    total: trans.length
                }
                setTransactionConnection(transactions)
            }
        }
    }, [])

    const handleConnect = async (values: any) => {
        let tranInfo: any = {};
        try {
            const syntax = values.syntax?.trim();
            tranInfo = parseSyntax(syntax);
        } catch (error: any) {
            toastError(error.toString());
            return;
        }

        if (!values.dataPack) {
            toastError('Vui lòng chọn gói cước!');
            return;
        }

        const [pCode, pType, price] = values.dataPack.split('::');
        const m = pCode.match(reNumberMonthDataPack);
        if (!m) {
            toastError('Mã gói cước không hợp lệ!');
            return;
        }

        const packMonth = m[1] ? Number(m[1]) : 0;
        const userMonth = tranInfo.numberMonth ? Number(tranInfo.numberMonth) : 0;

        if (packMonth > 0) {
            if (userMonth === 0) {
                toastError(`Gói cước này yêu cầu ${packMonth} tháng!`);
                return;
            }
            if (userMonth !== packMonth) {
                toastError('Gói cước không chính xác!');
                return;
            }
        } else {
            if (userMonth > 0) {
                toastError('Gói cước không hỗ trợ số tháng!');
                return;
            }
        }

        try {
            setIsLoading(true)
            const req = {
                cpCode: values.cpCode,
                dataPack: pCode,
                packType: pType,
                phoneAgency: tranInfo.phoneAgency,
                phoneSim: tranInfo.phoneSim,
                serialSim: tranInfo.serialSim,
                phoneCustomer: tranInfo.phoneCustomer,
                startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                customerName: tranInfo.customerName || '',
            }
            const resp = await apiAddTransactionConnection(req)
            const respTran = {
                ...req,
                customerName: tranInfo.customerName,
                price: resp.data?.transaction?.price || price,
                message: resp.data?.message,
                transaction: resp.data?.transaction,
            }
            const listTrans = [respTran, ...transactionConnection.transactions]
            const transactions = {
                transactions: listTrans,
                total: listTrans.length
            }
            setTransactionConnection(transactions)
            if (resp.data.status !== '200') {
                toastError(resp.data.message)
                return
            }
            if (tranInfo.customerName) {
                fetchInvoices(listTrans)
            }
            toastSuccess('Thêm đấu nối mới thành công!')
            formConnect.setFieldValue('dataPack', '')
            formConnect.setFieldValue('syntax', '')
            formConnect.setFieldValue('startDate', '')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleFilterPartnerPackage = (value: string) => {
        formConnect.setFieldValue('dataPack', '')
        setCpCode(value)
    }

    const handleInvoiceExport = async () => {
        try {
            if (!invoiceExportValue.customer_name || !modalInvoiceExport.data?.transaction?.id || !invoiceExportValue.invoice_template_id) {
                toastError('Vui lòng điền đầy đủ thông tin!')
                return
            }
            setIsLoading(true)
            const resp = await apiAddInvoice({
                tran_id: modalInvoiceExport.data?.transaction?.id,
                customer_name: invoiceExportValue.customer_name.toUpperCase(),
                invoice_template_id: invoiceExportValue.invoice_template_id,
            })
            setTransactionConnection({
                transactions: transactionConnection.transactions.map((item: any) => {
                    if (item.transaction?.id === modalInvoiceExport.data?.transaction?.id) {
                        item.invoice = resp.data?.invoice
                        item.customerName = invoiceExportValue.customer_name.toUpperCase()
                    }
                    return item
                }),
                total: transactionConnection?.transactions?.length
            })
            setModalInvoiceExport({ isOpen: false, data: null })
            setInvoiceExportValue({ customer_name: '', invoice_template_id: 0 })
            setShowModalConfirm(false)
            toastSuccess('Xuất hóa đơn thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra khi xuất nháp!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleInvoiceExportAdapter = async () => {
        try {
            if (modalInvoiceExport.data?.invoice?.status !== 'CREATED') {
                toastError('Hoá đơn phải được tạo nháp trước khi xuất!')
                return
            }
            setIsLoading(true)
            const resp = await apiAddInvoiceAdapterByInvoiceId({
                id: modalInvoiceExport.data?.invoice?.id,
            })
            setTransactionConnection({
                ...transactionConnection,
                transactions: transactionConnection.transactions.map((item: any) => {
                    if (item.transaction?.id === modalInvoiceExport.data?.transaction?.id) {
                        item.invoice = resp.data?.invoice
                    }
                    return item
                })
            })
            setShowModalConfirmExport(false)
            setModalInvoiceExport({ isOpen: false, data: null })
            toastSuccess('Xuất hóa đơn thành công!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleInvoicePrint = async () => {
        try {
            if (modalInvoiceExport.data?.invoice?.status !== 'SUCCESS') {
                toastError('Hóa đơn chưa xuất, vui lòng xuất hóa đơn trước khi tải về!')
                return
            }
            setIsLoading(true)
            const resp = await apiGetPrintInvoiceAdapter({
                id: modalInvoiceExport.data.invoice.invoice_id
            })
            saveAs(resp.data, modalInvoiceExport.data.invoice.invoice_id + ".pdf");
            toastSuccess('Đã tải xuống!')
        } catch (error) {
            toastError('Có lỗi xảy ra!')
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div>
            <Form form={formConnect} onFinish={handleConnect}>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item style={{ flex: 1, minWidth: 200 }} name='cpCode' label='Công ty'
                        rules={[{ required: true, message: 'Vui lòng chọn công ty!' }]}>
                        <Select
                            onChange={handleFilterPartnerPackage}
                            style={{ width: '100%' }}
                            showSearch
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="dataPack" label="Gói cước"
                        style={{ flex: 1, minWidth: 200 }}
                        rules={[{ required: true, message: 'Vui lòng chọn gói cước!' }]}>
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            disabled={!cpCode}
                            options={partnerPackageOptions?.filter((item: any) => {
                                if (filterPack) {
                                    return item.label?.toLowerCase().includes(filterPack.toLowerCase())
                                }
                                return true
                            })}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item style={{ maxWidth: 200 }} label="Lọc gói cước">
                        <Input onChange={e => setFilterPack(e.target.value)} autoComplete="off" />
                    </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item name="syntax" label="Cú pháp"
                        style={{ flex: 2, minWidth: 200 }}
                        rules={[{ required: true, message: 'Vui lòng nhập cú pháp!' }]}>
                        <Input autoComplete="off" />
                    </Form.Item>
                    {account?.canSelectDateConnect && (<>
                        <Form.Item>
                            <Tooltip title="Hiển thị ngày đấu nối">
                                <Checkbox onChange={() => {
                                    setIsOpenSelectDate(!isOpenSelectDate)
                                    formConnect.setFieldValue('startDate', '')
                                }} />
                            </Tooltip>
                        </Form.Item>
                        {isOpenSelectDate && (
                            <Form.Item name="startDate" label="Ngày đấu nối">
                                <DatePicker />
                            </Form.Item>
                        )}
                    </>
                    )}
                </div>
                <div style={{ textAlign: 'center' }}>
                    <Button htmlType="submit" icon={<CheckOutlined />} style={{ background: '#008965', color: 'white' }}>
                        <b>Xác nhận</b>
                    </Button>
                </div>
            </Form>
            <Divider />
            <CustomTable
                namePage='giao dịch'
                columns={transactionConnectionResponseColumns}
                dataSource={transactionConnection.transactions}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={transactionConnection.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            {modalInvoiceExport.data?.invoice ? (
                <Modal
                    width={'64%'}
                    title='Thông tin hóa đơn'
                    open={modalInvoiceExport.isOpen}
                    footer={null}
                    onCancel={() => setModalInvoiceExport({ isOpen: false, data: null })}>
                    <Divider />
                    <div>
                        <Descriptions
                            items={[
                                {
                                    key: 'tran_id',
                                    label: 'Mã giao dịch',
                                    children: modalInvoiceExport.data?.invoice?.tran_id,
                                    span: 2,
                                },
                                {
                                    key: 'invoice_id',
                                    label: 'Mã hoá đơn',
                                    children: modalInvoiceExport.data?.invoice?.invoice_id,
                                },
                            ]}
                        />
                        <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <b>Khách hàng:</b>
                            <span>{modalInvoiceExport.data?.invoice.customer_name}</span>
                        </p>
                        <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <b>Gói cước:</b>
                            <span>{modalInvoiceExport.data?.dataPack}</span>
                        </p>
                        <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <b>Giá:</b>
                            <span>{formatCurrency(Number(modalInvoiceExport.data?.price))}</span>
                        </p>
                        <Card size='small' style={{ marginTop: 10 }}>
                            {invoiceTemplates.filter((config: any) => config.id === modalInvoiceExport.data?.invoice.invoice_template_id).map((config: any, index: number) => (
                                <div key={index}>
                                    <Descriptions
                                        items={[
                                            {
                                                key: 'cpCode',
                                                label: 'Đối tác',
                                                children: config?.cpCode,
                                            },
                                            {
                                                key: 'network',
                                                label: 'Network',
                                                children: config?.network,
                                            },
                                            {
                                                key: 'invoice_series',
                                                label: 'Mẫu số',
                                                children: config?.invoice_series,
                                            },
                                            {
                                                key: 'tran_amount',
                                                label: 'Số tiền giao dịch',
                                                children: formatCurrency(config?.tran_amount),
                                            },
                                            {
                                                key: 'total_amount',
                                                label: 'Tổng số tiền',
                                                children: formatCurrency(config?.total_amount),
                                            },
                                            {
                                                key: 'tax_rate',
                                                label: 'Thuế suất',
                                                children: config?.tax_rate + '%',
                                            },
                                            {
                                                key: 'currency_code',
                                                label: 'Loại tiền tệ',
                                                children: config?.currency_code,
                                            },
                                            {
                                                key: 'exchange_rate',
                                                label: 'Tỷ giá',
                                                children: config?.exchange_rate,
                                            },
                                            {
                                                key: 'payment_method_name',
                                                label: 'Hình thức thanh toán',
                                                children: config?.payment_method_name,
                                            },
                                            {
                                                key: 'alias',
                                                label: 'Tên template',
                                                children: config?.alias,
                                            },
                                        ]}
                                    />
                                    <CustomTable
                                        namePage='sản phẩm'
                                        columns={invoiceTemplateItemsColumns}
                                        dataSource={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)}
                                        total={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)?.length}
                                        isHiddenPagination={true}
                                    />
                                </div>
                            ))}
                        </Card>
                    </div>
                    <Divider />
                    <div style={{ textAlign: 'right' }}>
                        {modalInvoiceExport.data?.invoice.status === 'SUCCESS' ? (
                            invoiceTemplates.find((config: any) => config.id === modalInvoiceExport.data?.invoice?.invoice_template_id)?.network === 'OTEK-MISA-QUICK' ? <></> :
                                <Button disabled={!account?.canGetPrintInvoiceAdapter} type='primary' onClick={handleInvoicePrint} icon={<DownloadOutlined />}>Tải xuống</Button>
                        ) : modalInvoiceExport.data?.invoice.status === 'CREATED' ? (
                            <Button disabled={!account?.canAddInvoiceAdapterByInvoiceId} type='primary' onClick={() => setShowModalConfirmExport(true)} icon={<PrinterOutlined />}>Xuất</Button>
                        ) : <></>}
                    </div>
                </Modal>
            ) : (
                <Modal
                    title='Tạo hoá hóa đơn nháp'
                    open={modalInvoiceExport.isOpen}
                    footer={null}
                    onCancel={() => setModalInvoiceExport({ isOpen: false, data: null })}>
                    <Divider />
                    <div>
                        <Descriptions
                            items={[
                                {
                                    key: 'phoneSim',
                                    label: 'Msisdn',
                                    children: modalInvoiceExport?.data?.phoneSim,
                                },
                                {
                                    key: 'serialSim',
                                    label: 'Serial Sim',
                                    children: modalInvoiceExport?.data?.serialSim,
                                },
                            ]}
                        />
                        <div style={{ marginBottom: 10 }}>Họ và tên KH</div>
                        <Input
                            value={invoiceExportValue.customer_name}
                            onChange={e => setInvoiceExportValue({ ...invoiceExportValue, customer_name: e.target.value })}
                        />
                        <div style={{ marginTop: 10 }}>
                            <div style={{ marginBottom: 10 }}>Chọn loại hoá đơn</div>
                            <Select
                                style={{ width: '100%' }}
                                value={invoiceExportValue.invoice_template_id}
                                onChange={value => setInvoiceExportValue({ ...invoiceExportValue, invoice_template_id: value })}
                                options={invoiceTemplates.filter((config: any) => config.cpCode === modalInvoiceExport?.data?.cpCode).map((config: any) => ({
                                    label: config.alias,
                                    value: config.id,
                                }))}
                            />
                        </div>
                    </div>
                    <Divider />
                    <div style={{ textAlign: 'right' }}>
                        <Button disabled={!account?.canAddInvoice} type='primary' onClick={() => {
                            if (!invoiceExportValue.customer_name || !invoiceExportValue.invoice_template_id) {
                                toastError('Vui lòng điền đầy đủ thông tin!')
                                return
                            }
                            setShowModalConfirm(true)
                        }}>Xuất nháp</Button>
                    </div>
                </Modal>
            )}
            <Modal
                width={'64%'}
                title='Xác nhận xuất hoá đơn nháp' open={showModalConfirm}
                onCancel={() => setShowModalConfirm(false)}
                footer={[
                    <Button onClick={handleInvoiceExport}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={() => setShowModalConfirm(false)}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Divider />
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Khách hàng:</b>
                    <span>{invoiceExportValue.customer_name}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Gói cước:</b>
                    <span>{modalInvoiceExport.data?.dataPack}</span>
                </p>
                <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Giá:</b>
                    <span>{formatCurrency(Number(modalInvoiceExport.data?.price))}</span>
                </p>
                <Card size='small' style={{ marginTop: 10 }}>
                    {invoiceTemplates.filter((config: any) => config.id === invoiceExportValue.invoice_template_id).map((config: any, index: number) => (
                        <div key={index}>
                            <Descriptions
                                items={[
                                    {
                                        key: 'cpCode',
                                        label: 'Đối tác',
                                        children: config?.cpCode,
                                    },
                                    {
                                        key: 'network',
                                        label: 'Network',
                                        children: config?.network,
                                    },
                                    {
                                        key: 'invoice_series',
                                        label: 'Mẫu số',
                                        children: config?.invoice_series,
                                    },
                                    {
                                        key: 'tran_amount',
                                        label: 'Số tiền giao dịch',
                                        children: formatCurrency(config?.tran_amount),
                                    },
                                    {
                                        key: 'total_amount',
                                        label: 'Tổng số tiền',
                                        children: formatCurrency(config?.total_amount),
                                    },
                                    {
                                        key: 'tax_rate',
                                        label: 'Thuế suất',
                                        children: config?.tax_rate + '%',
                                    },
                                    {
                                        key: 'currency_code',
                                        label: 'Loại tiền tệ',
                                        children: config?.currency_code,
                                    },
                                    {
                                        key: 'exchange_rate',
                                        label: 'Tỷ giá',
                                        children: config?.exchange_rate,
                                    },
                                    {
                                        key: 'payment_method_name',
                                        label: 'Hình thức thanh toán',
                                        children: config?.payment_method_name,
                                    },
                                    {
                                        key: 'alias',
                                        label: 'Tên template',
                                        children: config?.alias,
                                    },
                                ]}
                            />
                            <CustomTable
                                namePage='sản phẩm'
                                columns={invoiceTemplateItemsColumns}
                                dataSource={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)}
                                total={invoiceTemplateItems?.filter((i: any) => i.invoice_template_id === config?.id)?.length}
                                isHiddenPagination={true}
                            />
                        </div>
                    ))}
                </Card>
                <Divider />
            </Modal>
            <Modal
                title='Xác nhận xuất hoá đơn' open={showModalConfirmExport}
                onCancel={() => setShowModalConfirmExport(false)}
                footer={[
                    <Button onClick={handleInvoiceExportAdapter}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={() => setShowModalConfirmExport(false)}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Divider />
                {invoiceTemplates.filter((config: any) => config.id === modalInvoiceExport.data?.invoice?.invoice_template_id).map((config: any, index: number) => (
                    <p key={index}>Xuất hoá đơn <b>{config?.alias}</b> cho khách hàng <b>{modalInvoiceExport.data?.invoice?.customer_name}</b></p>
                ))}
                <Divider />
            </Modal>
        </div>
    )
}
